import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import type { Activity, Task } from "@/shared/cache";
import { useApi } from "@/shared/utils/request";
import { actingHeaders } from "@/v2/hooks/actions/types";

const STALE_MS = 5 * 60 * 1000;

export type { Activity, Task };

type TaskOptionsResponse = { required: boolean; options: Task[] };
type ActivityOptionsResponse = { required: boolean; options: Activity[] };

type UseTaskOptionsResult = {
	tasks: Task[];
	required: boolean;
	isLoading: boolean;
	/**
	 * The fetch failed. Distinct from an empty `tasks`: on failure the list is
	 * also empty, and callers that offer a clearing option must not present that
	 * as "this firm has no codes".
	 */
	isError: boolean;
};

type UseActivityOptionsResult = {
	activities: Activity[];
	required: boolean;
	isLoading: boolean;
	/** See UseTaskOptionsResult.isError. */
	isError: boolean;
};

function codeOptionsKey(
	kind: "tasks" | "activities",
	userId?: string,
	matterId?: string,
	timekeeperId?: string | null,
) {
	return [
		"v2",
		"code-options",
		kind,
		userId ?? "anonymous",
		matterId ?? "",
		timekeeperId ?? "self",
	] as const;
}

export function useTaskOptions(
	matterId: string,
	timekeeperId?: string | null,
): UseTaskOptionsResult {
	const { user } = useAuth();
	const { makeApiRequest } = useApi();
	const normalizedMatterId = matterId || "";

	const query = useQuery({
		queryKey: codeOptionsKey(
			"tasks",
			user?.id,
			normalizedMatterId,
			timekeeperId,
		),
		queryFn: async (): Promise<TaskOptionsResponse> => {
			const res = await makeApiRequest({
				endpoint: "/v2/code-options/tasks",
				method: "GET",
				queryParams: normalizedMatterId
					? { matterId: normalizedMatterId }
					: undefined,
				headers: actingHeaders(timekeeperId),
			});
			if (res.status === "ERROR") {
				throw new Error(res.error || "Failed to fetch task options");
			}
			return (
				res.data ?? {
					required: false,
					options: [],
				}
			);
		},
		enabled: Boolean(user?.id),
		staleTime: STALE_MS,
		refetchOnWindowFocus: false,
	});

	return {
		tasks: query.data?.options ?? [],
		required: query.data?.required ?? false,
		isLoading: query.isPending,
		isError: query.isError,
	};
}

export function useActivityOptions(
	matterId: string,
	timekeeperId?: string | null,
): UseActivityOptionsResult {
	const { user } = useAuth();
	const { makeApiRequest } = useApi();
	const normalizedMatterId = matterId || "";

	const query = useQuery({
		queryKey: codeOptionsKey(
			"activities",
			user?.id,
			normalizedMatterId,
			timekeeperId,
		),
		queryFn: async (): Promise<ActivityOptionsResponse> => {
			const res = await makeApiRequest({
				endpoint: "/v2/code-options/activities",
				method: "GET",
				queryParams: normalizedMatterId
					? { matterId: normalizedMatterId }
					: undefined,
				headers: actingHeaders(timekeeperId),
			});
			if (res.status === "ERROR") {
				throw new Error(res.error || "Failed to fetch activity options");
			}
			return (
				res.data ?? {
					required: false,
					options: [],
				}
			);
		},
		enabled: Boolean(user?.id),
		staleTime: STALE_MS,
		refetchOnWindowFocus: false,
	});

	return {
		activities: query.data?.options ?? [],
		required: query.data?.required ?? false,
		isLoading: query.isPending,
		isError: query.isError,
	};
}
